import { createContext, useContext, useState } from 'react'
import { User } from '@interfaces/user.interface'
import { localStorage } from '@services/local-storage.service'
import { StorageKeys } from '@constants/storage-keys.constant'

interface UserContextType {
  user: User | null
  setUser: (user: User | null) => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<User | null>(() => {
    const stored = localStorage.getItem<User>(StorageKeys.USER)
    return stored || null
  })

  /**
   * Set the user in the context
   * @param newUser - The new user to set
   */
  const setUser = (newUser: User | null) => {
    setUserState(newUser)
    if (newUser) {
      localStorage.setItem(StorageKeys.USER, newUser)
    } else {
      localStorage.removeItem(StorageKeys.USER)
    }
  }

  return <UserContext.Provider value={{ user, setUser }}>{children}</UserContext.Provider>
}

/**
 * Use the user context
 * @returns The user context
 */
export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
